import { ChangeDetectorRef, Component, computed, effect, ElementRef, inject, signal, untracked, viewChild, viewChildren } from '@angular/core';
import { SelectedAttributeService } from '../../services/selected-attribute.service';
import { Form } from 'src/app/interfaces/form';
import { UnavailableDialogComponent } from '../unavailable.dialog/unavailable.dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { FormsService } from 'src/app/services/forms/forms.service';
import { getAssetURL } from 'src/app/constants/assets';
import { GoogleAnalyticsService } from 'src/app/services/google-analytics/google-analytics.service';
import { Subscription } from 'rxjs';
import { LoadingService } from 'src/app/services/loading/loading.service';
import { RulesService } from 'src/app/services/rules/rules.service';
import { CheckoutService } from '../../services/checkout.service';
import { Navigation2Service } from '../../services/navigation2.service';
import { ScrollService } from 'src/app/services/scroll/scroll.service';
import { ProfileGroup } from 'src/app/interfaces/profile-group';
import { ProfileWeight } from 'src/app/interfaces/profile-weight';
import { ProfileGroupsService } from 'src/app/services/profile-groups/profile-groups.service';
import { ProfileWeightsService } from 'src/app/services/profile-weights/profile-weights.service';
import { formatWeightThickness } from 'src/app/utils/weight-thickness-format';
import {
  ProfileGroupMember,
  defaultMemberFor,
  groupHasMemberInSet,
  membersInSet,
  resolveMember,
  shouldShowWeightSelector,
  weightsForGroup,
} from './profile-group-resolution';

/** One button in the weight selector: the member it selects, its label row, and whether it is pickable. */
export interface WeightOption {
  member: ProfileGroupMember;
  weight: ProfileWeight | null;
  label: string;
  /** Pickable — available now, OR reachable after re-resolution. Gates the disabled state (#9883). */
  isSelectable: boolean;
  /** Pickable WITHOUT re-resolving. False means picking it clears engraving and finger size. */
  isAvailable: boolean;
  isSelected: boolean;
}

@Component({
  selector: 'app-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.scss']
})
export class FormComponent {
  private scrollService = inject(ScrollService);
  private navigationService = inject(Navigation2Service);
  private checkoutService = inject(CheckoutService);
  private rulesService = inject(RulesService);
  private loadingService = inject(LoadingService);
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private formService = inject(FormsService);
  private selectedAttributeService = inject(SelectedAttributeService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);
  private formCards = viewChildren('FormCard', { read: ElementRef<HTMLDivElement> });
  private container = viewChild('Container', { read: ElementRef });
  private undoRedoChangeSubscription: Subscription | null = null;
  shouldScroll = signal(true);
  // forms = FORMS.filter(f => VALID_ITEMS.some(v => v.form === f.shortName));
  forms = computed(() => this.formService.values());
  // formsToShow: Form[] = [];
  selectedForm = computed(() => this.selectedAttributeService.getValue().form);
  shouldAnimateSelection = false;
  // availableForms = computed(() => this.selectedAttributeService.availableAttributes().form);
  availableForms = signal<number[]>([]);
  reachableForms = signal<number[]>([]);
  filteredForms = signal<Form[]>([]);
  formsToShow = computed(() => {
    const isShowingUnavailable = this.selectedAttributeService.shouldShowUnavailable();
    const reachableForms = this.reachableForms();
    const filteredForms = this.filteredForms();
    const forms = this.forms();
    const formsToUse = filteredForms.length > 0 ? filteredForms : forms;
    if (isShowingUnavailable) {
      return formsToUse;
    }
    const selectedFormName = this.selectedForm()?.displayName;
    return formsToUse.filter(f => reachableForms.includes(f.id) || f.displayName === selectedFormName);
  });

  // ─── Profile groups (ADO #9683) ────────────────────────────────────────────────────────────
  // The grid renders GROUPS; a weight selector beneath the selected card resolves the group to
  // exactly one profile. Once a weight is selected it is just a profile, so everything below
  // mutateForm is unchanged.

  private profileGroupsService = inject(ProfileGroupsService);
  private profileWeightsService = inject(ProfileWeightsService);

  profileGroups = computed(() => this.profileGroupsService.values());
  profileWeights = computed(() => this.profileWeightsService.values());

  // 🛑 C1: selected group and weight are DERIVED from the profile, never held independently.
  // selected-attribute.service re-resolves `form` by id during conflict resolution at eight
  // sites, bypassing this component entirely. An independent WritableSignal would not observe
  // those writes and would display a group that no longer matches the selected profile.
  selectedGroup = computed<ProfileGroup | null>(() => {
    const groupID = this.selectedForm()?.formGroupID;
    if (groupID === null || groupID === undefined) {
      return null;
    }
    return this.profileGroups().find(g => g.id === groupID) ?? null;
  });

  // 🛑 THE INVARIANT THIS PROTECTS (ADO #9883), and it is the one to test rather than to reason
  // about: ENUMERATE EVERY PROFILE REACHABLE BY CLICKING THROUGH GROUPS AND WEIGHTS. THAT SET
  // MUST EQUAL THE SET REACHABLE FROM THE OLD FLAT PROFILE GRID. Grouping changes how profiles
  // are ORGANISED, never which ones can be reached.
  //
  // `availableForms` = selectable with the CURRENT configuration untouched.
  // `reachableForms` = reachable if other attributes are changed too.
  //
  // The flat grid showed a profile when it was REACHABLE (`formsToShow`, above, still does), and
  // clicking a reachable-but-unavailable one re-resolved the configuration. Keying group
  // visibility on AVAILABLE instead — which is what this did until 2026-09-17 — silently removed
  // those profiles from the product: a group whose only members were reachable rendered nowhere,
  // and a non-default member could not be reached at all because the weight selector is the only
  // route to it.
  //
  // 🛑 SO THIS IS JUST `reachableForms` — THE SAME LIST THE FLAT GRID USES. Deliberately not
  // `available ∪ reachable`: a union would only equal the flat grid's set if available ⊆
  // reachable, and that is an assumption about the rules service that nothing here establishes.
  // Using the identical list the flat grid uses makes the invariant hold by CONSTRUCTION and
  // needs no assumption at all. (The first cut of this fix did take the union, and would have
  // rested on that unestablished premise.)
  //
  // The two come from different endpoints and answer different questions:
  //   availableForms  <- getValidAttributes -> Rules/ValidForms   "valid with the CURRENT config"
  //   reachableForms  <- getReachableForms                        "reachable if other attrs move"
  // Neither endpoint was modified by #9490 — the rules code last changed 2026-02-18 / 2026-07-29.
  //
  // ⚠ WHAT THAT COSTS, deliberately accepted: selecting a reachable-but-not-valid member runs
  // setClosestByForm, which nulls engravingText, engravingType and size. That was the pre-grouping
  // behaviour; the no-clearing guarantee introduced by #9683 was paid for by hiding profiles, and
  // #9883 reverses that trade. selectGroup still PREFERS a valid member, so a tap only re-resolves
  // when nothing else reaches.
  //
  // The 2026-09-15 inert-card defect must not return: a rendered, non-disabled card always
  // selects. That now holds because visibility and clickability are the SAME predicate — which is
  // the real lesson from that bug, rather than "narrow the predicate".
  private selectableForms(): number[] {
    return this.reachableForms();
  }

  // Both delegate to the PURE predicates in profile-group-resolution, so the reachability
  // invariant is proved against the same code that runs here — see that module's header and
  // dev/ado-9683-smoke/reachability.test.mjs. Re-implementing either of these inline is how the
  // two grids came to disagree in the first place.
  private visibleMembers(group: ProfileGroup): ProfileGroupMember[] {
    const selectedID = this.selectedForm()?.id;
    const ids = selectedID === undefined ? this.selectableForms()
      : [...this.selectableForms(), selectedID];
    return membersInSet(group, ids, this.profileWeights());
  }

  /** A group is offerable if anything in it can be reached — available now, or after re-resolution. */
  private hasSelectableMember(group: ProfileGroup): boolean {
    return groupHasMemberInSet(group, this.selectableForms());
  }

  // R1: order by sortPosition, then displayName, then id. The tiebreak is NOT decoration —
  // ProfileGroups.Position comes from a NetSuite list that is empty, so every group sits at
  // 99999 and ordering on position alone is ordering on a constant: the grid would reorder
  // itself between syncs.
  //
  // R4: "a group with no AVAILABLE members is hidden entirely — there is no use showing a group
  // that cannot be selected." Keyed on hasSelectableMember, not on visibleMembers, so a group can
  // never render inert. The selected group is always kept (it is by definition selectable, but
  // stated explicitly so a conflict-resolution write cannot make the current selection disappear),
  // and with `shouldShowUnavailable` on the rest return greyed.
  groupsToShow = computed<ProfileGroup[]>(() => {
    const isShowingUnavailable = this.selectedAttributeService.shouldShowUnavailable();
    const selectedGroupID = this.selectedGroup()?.id;
    return [...this.profileGroups()]
      .sort((a, b) =>
        (a.sortPosition - b.sortPosition) ||
        (a.displayName < b.displayName ? -1 : a.displayName > b.displayName ? 1 : 0) ||
        (a.id - b.id))
      .filter(g =>
        this.hasSelectableMember(g) ||
        g.id === selectedGroupID ||
        (isShowingUnavailable && this.visibleMembers(g).length > 0));
  });

  // R2: no selector for a single-member group. Returns [] in that case, and the template
  // renders nothing rather than a control with one option.
  weightOptions = computed<WeightOption[]>(() => {
    const group = this.selectedGroup();
    if (!group || !shouldShowWeightSelector(group)) {
      return [];
    }
    const members = this.visibleMembers(group);
    if (members.length < 2) {
      return [];
    }
    const weights = this.profileWeights();
    const available = this.availableForms();
    const selectable = this.selectableForms();
    const selectedID = this.selectedForm()?.id;
    return members.map(member => {
      const weight = weights.find(w => w.id === member.formWeightID) ?? null;
      return {
        member,
        weight,
        // A weight row can be ABSENT: retiring a weight upstream is an inactivation, which drops
        // it from the push while profiles keep pointing at it. There is then no name to show, so
        // fall back to the member's own shortName — short enough for a toggle, and diagnosable,
        // rather than a blank button or a crash.
        label: formatWeightThickness(weight?.shortName ?? member.shortName, member.thicknessMM),
        // #9883: SELECTABLE, not available. A merely-reachable member is still a profile the
        // flat grid let you pick; disabling its option here is what made non-default members
        // unreachable, since the selector is the only route to them.
        isSelectable: selectable.includes(member.id),
        // Kept separately because it is genuinely different information: selecting a reachable
        // -but-unavailable member RE-RESOLVES and clears engraving and finger size. Nothing
        // renders it today (the flat grid gave no such hint either), but the distinction is the
        // one thing a future affordance would need, and deriving it later from `isSelectable`
        // would be impossible.
        isAvailable: available.includes(member.id),
        isSelected: member.id === selectedID,
      };
    });
  });

  // Greyed when nothing in it can be selected right now. That state is only reachable with
  // `shouldShowUnavailable` on, because R4 otherwise hides the group entirely.
  isGroupUnavailable(group: ProfileGroup): boolean {
    return !this.hasSelectableMember(group);
  }

  getGroupImageURL(group: ProfileGroup) {
    // ⚠ Do NOT cache this against the group id. The thumbnail is DERIVED from the group's
    // default member upstream, so it moves when that default changes.
    return group.thumbnail ? getAssetURL(group.thumbnail) : null;
  }

  // R3: the flagged default if it is available, otherwise the next available member in weight
  // order.
  //
  // #9883: PREFER AN AVAILABLE MEMBER, then fall back to a merely-reachable one. The two passes
  // matter — with one pass over available ∪ reachable, a group holding both would sometimes land
  // on the reachable member and re-resolve (clearing engraving and finger size) when an available
  // member was sitting right there. Bending is the fallback, never the first choice.
  async selectGroup(group: ProfileGroup) {
    const weights = this.profileWeights();
    const member = defaultMemberFor(group, this.availableForms(), weights)
      ?? defaultMemberFor(group, this.selectableForms(), weights);
    if (!member) {
      // Nothing in this group is reachable, so it should not have been rendered. Do nothing
      // rather than guess — and if this fires, the visibility predicate and this one disagree.
      return;
    }
    await this.selectMember(member, group.displayName);
  }

  // Takes the WEIGHT, not the member, and resolves it — because "profile group + weight is enough
  // to figure the profile" is the whole feature, and routing around resolveMember would leave the
  // one function that expresses it uncalled. `isSelectable` on the option gates the disabled
  // state; selectMember re-checks it, so a wrong template cannot select something unreachable.
  //
  // #9883: this path MAY now re-resolve, because the selector is the only route to a non-default
  // member and refusing here removed those profiles from the product entirely.
  async selectWeight(weightID: number | null) {
    const group = this.selectedGroup();
    if (!group) {
      return;
    }
    const member = resolveMember(group, weightID, this.profileWeights());
    if (!member) {
      // The group does not offer this weight — the selector should not have rendered it.
      return;
    }
    const weight = this.profileWeights().find(w => w.id === weightID) ?? null;
    await this.selectMember(member, weight?.displayName ?? member.shortName);
  }

  // ⚠ THIS USED TO REFUSE AN UNAVAILABLE MEMBER, and that refusal is what #9883 removed.
  //
  // The reasoning it replaced: setClosestByForm nulls engravingText, engravingType and size, and
  // that was judged "correct for 'I picked a different ring' and wrong under a small toggle
  // beneath a card". The judgement is defensible and the CONSEQUENCE was not: because the weight
  // selector is the only route to a non-default member, refusing here made every
  // reachable-but-unavailable profile unreachable in the product. Grouping stopped being a
  // reorganisation and became a reduction.
  //
  // The rule now, from Greyson 2026-09-17: enumerate every profile reachable by clicking through
  // groups and weights, and it must equal the set the flat grid reached. So this accepts anything
  // SELECTABLE and hands it to the same applyForm the flat grid uses.
  //
  // It still refuses a member that is neither available nor reachable — that is not a policy
  // choice, it is the case where no path exists.
  private async selectMember(member: Form, analyticsLabel: string) {
    if (!this.selectableForms().includes(member.id)) {
      return;
    }
    if (this.selectedForm()?.id === member.id) {
      return;
    }
    if (this.googleAnalyticsService.isInitialized) {
      this.googleAnalyticsService.triggerEvent('click', 'attribute_panel', 'profile', analyticsLabel);
    }
    await this.applyForm(member);
  }

  /** Exposed for the template's @for tracking. */
  trackGroup(_index: number, group: ProfileGroup) {
    return group.id;
  }

  // ─── Where the selector goes in the grid ───────────────────────────────────────────────────
  // 🛑 THE COLUMN COUNT IS MEASURED FROM THE LIVE GRID, NEVER DUPLICATED FROM THE SCSS.
  // form.component.scss sets 1/3/2 columns across three breakpoints, and this component already
  // carries a warning elsewhere in the codebase about exactly this: main-image.component
  // duplicates its breakpoints between variables.scss and TS, and the drift shows up as the
  // layout and its overlays disagreeing in a narrow band. getComputedStyle resolves the actual
  // track list, so there is nothing to drift.
  private formContainer = viewChild('FormContainerEl', { read: ElementRef<HTMLElement> });
  gridColumns = signal(1);

  private measureColumns() {
    const el = this.formContainer()?.nativeElement;
    if (!el) { return; }
    const tracks = getComputedStyle(el).gridTemplateColumns.trim();
    // "200px 200px" -> 2. A single "none"/empty resolves to 1.
    const n = tracks && tracks !== 'none' ? tracks.split(/\s+/).length : 1;
    if (n !== this.gridColumns()) { this.gridColumns.set(n); }
  }

  // The index of the LAST card on the selected card's row. The selector is emitted after that
  // card, not after the selected one — inserting a full-width row mid-row pushes the neighbouring
  // card onto the next line, which is visible as the grid reflowing when you pick a card in
  // column 1. Reported 2026-09-15.
  selectorAfterIndex = computed<number | null>(() => {
    const groups = this.groupsToShow();
    const selectedID = this.selectedGroup()?.id;
    if (selectedID === undefined) { return null; }
    const idx = groups.findIndex(g => g.id === selectedID);
    if (idx < 0) { return null; }
    const cols = Math.max(1, this.gridColumns());
    return Math.min(groups.length - 1, Math.floor(idx / cols) * cols + cols - 1);
  });

  // 1-based grid column of the selected card, so the select sits centred beneath it rather than
  // in the middle of a full-width row.
  selectedColumn = computed<number>(() => {
    const groups = this.groupsToShow();
    const selectedID = this.selectedGroup()?.id;
    const idx = groups.findIndex(g => g.id === selectedID);
    const cols = Math.max(1, this.gridColumns());
    return idx < 0 ? 1 : (idx % cols) + 1;
  });

  /** The weight currently selected, for the select's value. */
  selectedWeightID = computed<number | null>(() => this.selectedForm()?.formWeightID ?? null);

  // The control has no visible <mat-label>, so this IS its accessible name. It names the group so
  // a screen reader announces what is being chosen and for which profile — the association a
  // sighted user gets from the select sitting under the selected card. Falls back to a generic
  // name rather than "Weight for undefined" if the group somehow resolves to nothing.
  weightAriaLabel = computed<string>(() => {
    const name = this.selectedGroup()?.displayName;
    return name ? `Weight for ${name} profile` : 'Profile weight';
  });

  constructor() {
    effect(async () => {
      this.loadingService.load('FormComponent::GetReachable');
      const reachableAttributes = await this.rulesService.getReachableForms(this.selectedAttributeService.configuredItem(), untracked(() => this.checkoutService.isRetailerOnly()) ? this.checkoutService.retailerIframeID : null);
      this.reachableForms.set(reachableAttributes);
      this.loadingService.done('FormComponent::GetReachable');
    }, { allowSignalWrites: true });

    effect(async () => {
      const availableAttributes = await this.selectedAttributeService.availableAttributes();
      this.availableForms.set(availableAttributes.formIDs);
    }, { allowSignalWrites: true });

    effect(() => {
      this.navigationService.subtitle.set(this.selectedForm()?.displayName ?? null);
    }, { allowSignalWrites: true });

    effect(async () => {
      const cards = this.formCards();
      await this.reachableForms();
      const groups = this.groupsToShow();
      const container = this.container();
      if (untracked(() => this.shouldScroll()) && container) {
        this.scrollService.scrollToPosition('forms', container.nativeElement);
        // 🛑 COMPARE LIKE WITH LIKE — this is ADO #9875.
        //
        // The guard means "the DOM has not caught up with the data yet, so keep restoring"; once
        // the two agree it must latch off. `#FormCard` sits on *ngFor over groupsToShow(), so the
        // rendered card count converges on groupsToShow().length.
        //
        // This compared against reachableForms().length, which returns PROFILES — 21 of them
        // against 11 group cards on the fixture. Since #9683 turned this grid from a profile grid
        // into a GROUP grid the two populations can never converge, so shouldScroll stayed true
        // forever and this effect re-restored the saved position on EVERY recompute — i.e. on
        // every selection. The saved position is only written in ngOnDestroy, so within a single
        // visit it is 0, and the list jumped to the top.
        //
        // Measured 2026-09-16 (dev/ado-9683-smoke/scroll-probe.mjs): scrolled to 228, clicked a
        // group, scrollTop 0. Width, whose guard compares widths to width cards, held at 156.
        this.shouldScroll.set(groups.length !== cards.length);
      }
    }, { allowSignalWrites: true });
  }

  ngOnInit() {
    if (this.googleAnalyticsService.isInitialized) {
      this.googleAnalyticsService.triggerEvent('view', 'attribute_panel', 'section_view', 'form');
    }
  }

  ngAfterViewInit() {
    this.shouldAnimateSelection = true;
    // Measure the grid's real column count now and on every resize. A ResizeObserver is used
    // rather than a window resize listener because the panel can change width without the window
    // doing so, and because the first measurement must happen after layout rather than on a timer.
    this.measureColumns();
    const el = this.formContainer()?.nativeElement;
    if (el) {
      this.columnObserver = new ResizeObserver(() => this.measureColumns());
      this.columnObserver.observe(el);
    }
    this.changeDetectorRef.detectChanges();
  }

  private columnObserver: ResizeObserver | null = null;

  ngOnDestroy() {
    this.columnObserver?.disconnect();
    this.undoRedoChangeSubscription?.unsubscribe();
    const container = this.container();
    if (container) {
      this.scrollService.savePosition('forms', container.nativeElement.scrollTop);
    }
  }

  // ⚠ NO LONGER REACHED FROM THE TEMPLATE as of ADO #9683 — the grid renders GROUPS and goes
  // through selectGroup/selectWeight -> selectMember, which refuses an unavailable profile.
  // This method is kept because it is still the correct behaviour for "I picked a different
  // ring" if a flat profile grid is ever restored (e.g. a fallback for ungrouped profiles).
  //
  // 🛑 DO NOT WIRE THIS TO THE WEIGHT SELECTOR OR A GROUP CARD. Its else-branch calls
  // setClosestByForm, which nulls engravingText, engravingType and size. That is the exact path
  // rules R3 and R4 exist to make unreachable from this feature.
  async selectForm(form: Form) {
    if (this.googleAnalyticsService.isInitialized) {
      this.googleAnalyticsService.triggerEvent('click', 'attribute_panel', 'profile', form?.shortName);
    }

    if (this.selectedForm()?.displayName !== form.displayName) {
      await this.applyForm(form);
    }
  }

  // 🛑 ONE implementation of "make this profile the selected one", shared by the flat-grid path
  // and the group/weight path (#9883). It existed twice in spirit before — selectForm bent the
  // configuration and selectMember refused to — which is exactly how the two grids came to offer
  // different sets of profiles. Keeping one body is what makes "grouping changes organisation,
  // not reachability" true by construction rather than by review.
  //
  // available  -> mutateForm, configuration untouched.
  // reachable  -> setClosestByForm, which re-resolves and NULLS engravingText, engravingType and
  //               size. That is the flat grid's behaviour and is intended here; callers prefer an
  //               available member first so this is the fallback, not the default.
  private async applyForm(form: Form) {
    if (this.availableForms().includes(form.id)) {
      await this.selectedAttributeService.mutateForm(form);
      return;
    }
    try {
      this.loadingService.load('SetClosestByForm');
      await this.selectedAttributeService.setClosestByForm(form);
    } catch (e) {
      console.log(e);
      this.dialog.open(UnavailableDialogComponent, { maxWidth: 450, data: 'form' });
    } finally {
      this.loadingService.done('SetClosestByForm');
    }
  }

  getImageURL(form: Form) {
    if (form.thumbnail) {
      return getAssetURL(form.thumbnail);
    }
    return null;
  }

  valueExtractor(form: Form) {
    return form.displayName;
  }

  filterChange(filteredForms: any) {
    this.filteredForms.set(filteredForms);
  }
}
